/**
 * Collection Search Bar
 * ---------------------
 * Mevcut collection scope'unda title bazlı arama yapar.
 * Pagination temanın kendi markup'ını taklit eder (snippets/pagination.liquid).
 *
 * Akış:
 * 1. İlk aramada collection ürünlerini /collections/{handle}/products.json ile yükle
 * 2. Title/vendor/tags üzerinde JS tarafında filtreleme yap
 * 3. Eşleşenleri sayfalara böl (PRODUCTS_PER_PAGE)
 * 4. Sadece o anki sayfanın card HTML'lerini fetch et
 * 5. Pagination'ı temanın .pagination-wrapper'ında render et
 * 6. Search temizlenince orijinal grid + pagination geri gelir
 */

const PRODUCTS_PER_PAGE = 20;

class CollectionSearchBar extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-collection-search-input]');
    this.clearButton = this.querySelector('[data-collection-search-clear]');
    this.collectionHandle = this.dataset.collectionHandle;

    if (!this.input || !this.collectionHandle) {
      console.warn('[CollectionSearch] Input veya collection handle eksik.');
      return;
    }

    this.searchTerm = '';
    this.productsCache = null;
    this.productsLoading = null;
    this.cardCache = new Map();
    this.originalGridSnapshot = null;
    this.originalPaginationSnapshot = null; // .pagination-wrapper içeriği
    this.abortController = null;

    // Pagination state
    this.currentMatches = [];
    this.currentPage = 1;

    this.debouncedSearch = this.debounce(this.handleSearch.bind(this), 350);

    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.clearSearch.bind(this));
    }

    this.observeGridChanges();
  }

  onInput(event) {
    const value = event.target.value.trim();
    this.toggleClearButton(value.length > 0);
    this.debouncedSearch(value);
  }

  onKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.handleSearch(this.input.value.trim());
    }
    if (event.key === 'Escape') {
      this.clearSearch();
    }
  }

  async handleSearch(searchTerm) {
    if (searchTerm === this.searchTerm) return;
    this.searchTerm = searchTerm;
    this.currentPage = 1;

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    if (!searchTerm) {
      this.restoreOriginalGrid();
      return;
    }

    if (searchTerm.length < 2) {
      return;
    }

    try {
      this.captureOriginalSnapshots();
      this.showLoading();

      const products = await this.getCollectionProducts();
      if (this.searchTerm !== searchTerm) return;

      const matches = this.filterProducts(products, searchTerm);
      this.currentMatches = matches;

      if (matches.length === 0) {
        this.renderEmptyState();
        return;
      }

      await this.renderCurrentPage();
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[CollectionSearch] Hata:', err);
      this.hideLoading();
    }
  }

  async renderCurrentPage() {
    const start = (this.currentPage - 1) * PRODUCTS_PER_PAGE;
    const end = start + PRODUCTS_PER_PAGE;
    const pageProducts = this.currentMatches.slice(start, end);

    this.showLoading();

    const cardHtmls = await this.fetchCardHtmls(pageProducts.map(p => p.handle));

    if (!this.searchTerm) return;

    this.renderResults(cardHtmls);
    this.renderPagination();
  }

  async getCollectionProducts() {
    if (this.productsCache) return this.productsCache;
    if (this.productsLoading) return this.productsLoading;

    this.productsLoading = (async () => {
      const all = [];
      const MAX_PAGES = 20;

      for (let page = 1; page <= MAX_PAGES; page++) {
        const url = `/collections/${this.collectionHandle}/products.json?limit=250&page=${page}`;
        const response = await fetch(url);
        if (!response.ok) break;

        const data = await response.json();
        const products = data.products || [];
        if (products.length === 0) break;

        all.push(...products);
        if (products.length < 250) break;
      }

      this.productsCache = all;
      return all;
    })();

    return this.productsLoading;
  }

  filterProducts(products, searchTerm) {
    const normalize = (str) =>
      (str || '')
        .toLocaleLowerCase('tr-TR')
        .replace(/ı/g, 'i')
        .replace(/ş/g, 's')
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c');

    const term = normalize(searchTerm);
    const terms = term.split(/\s+/).filter(Boolean);

    return products.filter(product => {
      const haystack = normalize(
        [
          product.title,
          product.vendor,
          product.product_type,
          Array.isArray(product.tags) ? product.tags.join(' ') : product.tags
        ].filter(Boolean).join(' ')
      );

      return terms.every(t => haystack.includes(t));
    });
  }

  async fetchCardHtmls(handles) {
    const promises = handles.map(handle => this.fetchSingleCard(handle));
    const results = await Promise.all(promises);
    return results.filter(html => html !== null);
  }

  async fetchSingleCard(handle) {
    if (this.cardCache.has(handle)) {
      return this.cardCache.get(handle);
    }

    try {
      const url = `/products/${handle}?section_id=ajax-card-product`;
      const response = await fetch(url, { signal: this.abortController.signal });
      if (!response.ok) return null;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const card = doc.querySelector('.card-wrapper, .product-card-wrapper');
      if (!card) return null;

      const cardHtml = card.outerHTML;
      this.cardCache.set(handle, cardHtml);
      return cardHtml;
    } catch (err) {
      if (err.name === 'AbortError') throw err;
      return null;
    }
  }

  /**
   * Search yapılmadan ÖNCE orijinal grid + pagination snapshot'larını al.
   */
  captureOriginalSnapshots() {
    if (this.originalGridSnapshot === null) {
      const grid = document.getElementById('product-grid');
      if (grid) this.originalGridSnapshot = grid.innerHTML;
    }
    if (this.originalPaginationSnapshot === null) {
      const wrapper = document.querySelector('.pagination-wrapper');
      this.originalPaginationSnapshot = wrapper ? wrapper.innerHTML : '';
    }
  }

  renderResults(cardHtmls) {
    const grid = document.getElementById('product-grid');
    if (!grid) return;

    const wrapped = cardHtmls
      .map(html => `<li class="grid__item">${html}</li>`)
      .join('');

    grid.innerHTML = wrapped;

    this.hideLoading();
    this.reinitializeCardScripts(grid);
  }

  renderEmptyState() {
    const grid = document.getElementById('product-grid');
    if (!grid) return;
    grid.innerHTML = `
      <li class="grid__item collection-search-empty" style="grid-column: 1 / -1; text-align: center; padding: 40px 20px;">
        <p style="font-size: 16px; color: rgba(var(--color-foreground), 0.7);">
          "<strong>${this.escapeHtml(this.searchTerm)}</strong>" için bu kategoride sonuç bulunamadı.
        </p>
      </li>
    `;
    this.clearPaginationWrapper();
    this.hideLoading();
  }

  /**
   * Pagination'ı temanın .pagination-wrapper'ı içine, kendi snippet markup'ıyla render eder.
   */
  renderPagination() {
    const wrapper = document.querySelector('.pagination-wrapper');
    if (!wrapper) return;

    const totalPages = Math.ceil(this.currentMatches.length / PRODUCTS_PER_PAGE);

    // Tek sayfa varsa wrapper'ı boşalt
    if (totalPages <= 1) {
      wrapper.innerHTML = '';
      return;
    }

    const parts = this.buildPaginationParts(this.currentPage, totalPages);

    let html = `
      <nav class="pagination" role="navigation" aria-label="Sayfalama">
        <div class="blog-count">
          Sayfa ${this.currentPage} / ${totalPages}
        </div>
        <ul class="pagination__list list-unstyled">
    `;

    // Önceki
    if (this.currentPage > 1) {
      html += `
        <li>
          <button type="button"
            class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce"
            data-search-page="${this.currentPage - 1}"
            aria-label="Önceki sayfa">
            <span>Önceki</span>
          </button>
        </li>
      `;
    }

    // Sayfa numaraları
    for (const part of parts) {
      if (part === '...') {
        html += `<li><span class="pagination__item">…</span></li>`;
      } else if (part === this.currentPage) {
        html += `
          <li>
            <button type="button"
              class="pagination__item pagination__item--current light"
              aria-current="page"
              aria-label="Sayfa ${part}"
              disabled>
              ${part}
            </button>
          </li>
        `;
      } else {
        html += `
          <li>
            <button type="button"
              class="pagination__item link"
              data-search-page="${part}"
              aria-label="Sayfa ${part}">
              ${part}
            </button>
          </li>
        `;
      }
    }

    // Sonraki
    if (this.currentPage < totalPages) {
      html += `
        <li>
          <button type="button"
            class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce"
            data-search-page="${this.currentPage + 1}"
            aria-label="Sonraki sayfa">
            <span>Sonraki</span>
          </button>
        </li>
      `;
    }

    html += `</ul></nav>`;
    wrapper.innerHTML = html;

    // Event delegation (önceki listener'lar wrapper.innerHTML değiştiği için temizlendi)
    wrapper.addEventListener('click', this.onPaginationClick.bind(this));
  }

  onPaginationClick(e) {
    const btn = e.target.closest('[data-search-page]');
    if (!btn) return;
    const page = parseInt(btn.dataset.searchPage, 10);
    if (!isNaN(page) && page !== this.currentPage) {
      this.goToPage(page);
    }
  }

  /**
   * Akıllı sayfa numarası dizisi üretir.
   * Örn: [1, '...', 4, 5, 6, '...', 15]
   */
  buildPaginationParts(current, total) {
    const delta = 1;
    const range = [];
    const result = [];

    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      }
    }

    let prev = 0;
    for (const i of range) {
      if (i - prev === 2) {
        result.push(prev + 1);
      } else if (i - prev > 2) {
        result.push('...');
      }
      result.push(i);
      prev = i;
    }

    return result;
  }

  async goToPage(page) {
    this.currentPage = page;
    await this.renderCurrentPage();
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      gridContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  clearPaginationWrapper() {
    const wrapper = document.querySelector('.pagination-wrapper');
    if (wrapper) wrapper.innerHTML = '';
  }

  restoreOriginalGrid() {
    const grid = document.getElementById('product-grid');
    const wrapper = document.querySelector('.pagination-wrapper');

    if (grid && this.originalGridSnapshot !== null) {
      grid.innerHTML = this.originalGridSnapshot;
      this.reinitializeCardScripts(grid);
    }

    if (wrapper && this.originalPaginationSnapshot !== null) {
      wrapper.innerHTML = this.originalPaginationSnapshot;
    }

    this.originalGridSnapshot = null;
    this.originalPaginationSnapshot = null;
    this.currentMatches = [];
    this.currentPage = 1;
    this.hideLoading();
  }

  showLoading() {
    const collectionEl = document.querySelector('#ProductGridContainer .collection');
    if (collectionEl) collectionEl.classList.add('loading');
  }

  hideLoading() {
    const collectionEl = document.querySelector('#ProductGridContainer .collection');
    if (collectionEl) collectionEl.classList.remove('loading');
  }

  reinitializeCardScripts(grid) {
    if (typeof window.lazySizes !== 'undefined' && window.lazySizes.loader) {
      window.lazySizes.loader.checkElems();
    }

    grid.querySelectorAll('img.lazyload').forEach(img => {
      if (img.dataset.src && !img.src) img.src = img.dataset.src;
      if (img.dataset.srcset && !img.srcset) img.srcset = img.dataset.srcset;
    });
  }

  observeGridChanges() {
    const grid = document.getElementById('product-grid');
    if (!grid) return;

    const observer = new MutationObserver(() => {
      if (this.searchTerm) return;
      this.originalGridSnapshot = null;
      this.originalPaginationSnapshot = null;
    });

    observer.observe(grid, { childList: true, subtree: false });
  }

  clearSearch() {
    this.input.value = '';
    this.toggleClearButton(false);
    this.handleSearch('');
    this.input.focus();
  }

  toggleClearButton(show) {
    if (!this.clearButton) return;
    this.clearButton.style.display = show ? 'flex' : 'none';
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  debounce(fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('collection-search-bar')) {
  customElements.define('collection-search-bar', CollectionSearchBar);
}